import React, { useState, useEffect, useCallback } from "react";
import qs from "query-string";
import _ from "lodash";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";

import { request } from "utils/proxy";
import { isFacetedSearchEmpty } from "utils/search";

import Tooltip from "components/Tooltip";
import InfoCard from "components/common/InfoCard/InfoCard";
import FacetedSearchBar from "components/FacetedSearchBar";

import infoCardStyles from "components/common/InfoCard/styles.css";
import { customFacetedSearchStyles } from "./styles";

const IntegrationComponent = props => {
	const { config, onChange, cardKeys, url, dictionary, helper, onEvent = _.noop } = props;

	const [ isLoading, setIsLoading ] = useState(true);
	const [ linkedItems, setLinkedItems ] = useState([]);
	const [ errorMessage, setErrorMessage ] = useState();
	const [ searchResults, setSearchResults ] = useState([]);
	const [ isComponetLoading, setComponetIsLoading ] = useState(true);
	const [ facetedInputValues , setFacetedInputValues ] = useState("");
	const [ facetedSearchResults, setFacetedSearchResults ] = useState([]);
	const [ shouldResetFacetedSearch, setShouldResetFacetedSearch ] = useState(false);

	useEffect(() => {
		const linkedIds = (config.value?.split(',') ?? []).filter(Boolean);

		if (isNonEmptyArray(linkedIds)) {
			helper.fetchData(linkedIds)
				.then(linkedData => {
					setLinkedItems(linkedData);
					setComponetIsLoading(false);
				}).catch(error => {
					if(error.response.status === 406){
						setErrorMessage(dictionary.errorMessage);
					}
					setComponetIsLoading(false);
				});
		} else {
			setLinkedItems([]);
			setComponetIsLoading(false);
		}
	}, [ config.value ]);

	useEffect(() => {
		setIsLoading(true);

		if (!isFacetedSearchEmpty({ facetSearch: facetedInputValues })) {
			const query = qs.stringify(facetedInputValues);

			request(`${url}?${query}`)
				.then(results => {
					setSearchResults(results);
					setFacetedSearchResults(helper.mapResults(results));
					setIsLoading(false);
				});
	  	}
	}, [ facetedInputValues ]);

	const onFocus = useCallback(() => {
		onEvent("Component.focus", config, {});
	}, [ onEvent, config ]);

	const linkItemsToSubmission = useCallback(idsToLink => {
		onFocus();
		onChange(idsToLink?.join(','));
	}, [ onChange, onFocus ]);

	const convertToCardItem = useCallback(item =>  {
		const cardItems = [];

		for (const [i, [cardKey, key ]] of Object.entries(cardKeys).entries()) {
			const value = item[cardKey] ?? "--";

			if(i === 0 && item.url) {
				cardItems.push({ key, value, url: item.url });
			} else {
				cardItems.push({ key, value });
			}
		}
		return cardItems;
	}, [ cardKeys ]);

	const onSelect =  useCallback(valueId => {
		const value = valueId && searchResults.find(searchResult => searchResult[helper.apiKey] === valueId);

		if (value) {
			const newLinkedItems = _.uniqBy([ ...linkedItems, value ], helper.apiKey);
			const ids = linkedItems.map(linkedItem => linkedItem[helper.apiKey]);
			const idsToLink =  _.uniq([ ...ids, valueId ]);

			if (valueId) {
				setLinkedItems(newLinkedItems);
				setFacetedSearchResults([]);
				setShouldResetFacetedSearch(true);
				linkItemsToSubmission(idsToLink);
			}
		}
	}, [ linkedItems, searchResults, facetedSearchResults, shouldResetFacetedSearch ]);

	const onSearchChange = useCallback(({ facetSearch = {} }) => {
		if (!isFacetedSearchEmpty({ facetSearch })) {
			setFacetedInputValues(facetSearch);
		}
	}, [ facetedInputValues ]);

	const onRemove = useCallback(item => {
		const newLinkedItems = linkedItems.filter(linkedItem => linkedItem[helper.apiKey] !== item[helper.apiKey]);
		const idsToLink = newLinkedItems.map(filteredFund => filteredFund[helper.apiKey]);

		if (item[helper.apiKey]) {
			setLinkedItems(newLinkedItems);
			linkItemsToSubmission(idsToLink);
		}
	}, [ linkedItems ]);

	return (<div style={{ margin: "25px 0 0 0" }}>
		{isComponetLoading ?
		<LoadingIndicator /> :
		<div>
			<Tooltip id={config.id} tooltipContent={config.tooltip || config.extraSettings.tooltip}>
				<h2>{dictionary.headline}</h2>
			</Tooltip>
			<p>{dictionary.description}</p>

			{ config.enabled && helper.searchFacets && (
				<FacetedSearchBar
					facets={helper.searchFacets}
					onChange={onSearchChange}
					results={facetedSearchResults}
					isResultsLoading={isLoading}
					onResultsSelect={onSelect}
					customStyles={customFacetedSearchStyles}
					shouldResetFacetedSearch={shouldResetFacetedSearch}
					placeholder="Type or select a search category..."
					type="INTEGRATION"
				/>
			)}

			{ isNonEmptyArray(linkedItems) ? (
				<>
					<div className={infoCardStyles.infocards}>
						{ linkedItems.map((item, k) => (
							<InfoCard
								key={k}
								items={convertToCardItem(item)}
								canEdit={config.enabled && helper.searchFacets}
								onClose={() => onRemove(item)}
							/>
						))}
					</div>
					{dictionary.infoText}
				</>
			) : errorMessage ?? dictionary.noData }
		</div>}
	</div>)
};

export default IntegrationComponent;
